import type {
  IErrorApiResponse,
  IPaginationParams,
  IPaginationResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  serialize,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useCallback } from 'react';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import type {
  IActiveQuoteService,
  ICreateQuoteServicePricingPolicyPayload,
  IDuplicatePolicyItem,
  IDuplicatePolicyPayload,
  IQuoteErrorResponse,
  IQuoteServicePricingPolicy,
  IServicePricingPolicyFilterQuery,
  IQuoteServiceType,
  IGetOneQuoteServicePricingPolicy,
} from '~/purchase/types';
import { COMPUTED_PRICING_POLICY_STATUS } from '~/common/schemas';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse<ICreateQuoteServicePricingPolicyPayload>,
    IQuoteErrorResponse<IValidationError<CommonErrorCodesEnumType>>,
    ICreateQuoteServicePricingPolicyPayload
  >;
  paginationFetcher?: {
    enabled?: boolean;
  };
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IActiveQuoteService>>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      serviceTypes: Array<IQuoteServiceType>;
      tyresMetadata: Array<{
        tyreCount?: number;
        diameter?: number;
        vehicleTypePublicKey?: string;
      }>;
    };
  };
  remove?: UseMutationOptions<
    ISuccessApiResponse<IQuoteServicePricingPolicy>,
    IQuoteErrorResponse,
    { policyGroupId: number }
  >;
  checkOverlap?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    {
      policyGroupId?: number;
      customerIds: Array<string>;
      startDate: string;
      endDate?: string | null;
      default: boolean;
    }
  >;

  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { id: string; body: IDuplicatePolicyPayload }
  >;
  getConflicted?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDuplicatePolicyItem>>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      id: number;
      customerIds: Array<string>;
    };
  };
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IGetOneQuoteServicePricingPolicy>,
      IErrorApiResponse
    > & { args: { policyGroupId: string } }
  >;
  update?: UseMutationOptions<
    ISuccessApiResponse<ICreateQuoteServicePricingPolicyPayload>,
    IQuoteErrorResponse<IValidationError<CommonErrorCodesEnumType>>,
    { id: string; body: ICreateQuoteServicePricingPolicyPayload }
  >;
  getActivePricingPolicies?: {
    enabled?: boolean;
  };
};

export const useManageQuoteServicePricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const createMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICreateQuoteServicePricingPolicyPayload>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.quoteServicePricingPolicies.base(),
      PurchaseQueryKeys.quoteDefaultServicePricingPolicies.base(),
    ],
    retry: false,
    ...options.create,
  });

  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteServicePricingPolicies.matching(
      idCustomer,
      options.list?.args,
    ),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<Array<IActiveQuoteService>>>(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/matching?${serialize(
              {
                ...options.list?.args,
                customerId: idCustomer,
              },
            )}`,
          )
        : emptyQueryError(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: false,
    ...options.list,
  });

  const paginationFetcher = useCallback(
    (query: IPaginationParams & IServicePricingPolicyFilterQuery) => {
      return callCoreApi<IPaginationResponse<IQuoteServicePricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies?${serialize(
          { ...query },
        )}`,
      );
    },
    [callCoreApi, pricingPoliciesDomainBaseUrl, tenantCode],
  );

  const getActivePricingPoliciesQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteServicePricingPolicies.active(idCustomer),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IQuoteServicePricingPolicy>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies?${serialize(
          {
            customerIds: [idCustomer],
            page: 1,
            // we cannot have more than 2 actives policies ( default & by customer)
            limit: 2,
            computedStatuses: [COMPUTED_PRICING_POLICY_STATUS.ACTIVE],
          },
        )}`,
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: true,
  });

  const removeMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IQuoteServicePricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/${variables.policyGroupId}`,
        { method: 'DELETE' },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.quoteServicePricingPolicies.base(
        state.targetCustomer?.id as string,
      ),
      PurchaseQueryKeys.quoteDefaultServicePricingPolicies.base(),
      [
        ...(idCustomer
          ? PurchaseQueryKeys.quoteServicePricingPolicies.active(idCustomer)
          : []),
      ],
    ],
    ...options.remove,
  });

  const checkOverlapMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/validations/overlap`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    ...options.checkOverlap,
  });

  const getConflictedQuery = useQuery({
    queryKey: PurchaseQueryKeys.quotePricingPolicies.conflicted(
      options.getConflicted?.args,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IDuplicatePolicyItem>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/${
          options.getConflicted?.args.id
        }/conflicted?${serialize({
          customerIds: options.getConflicted?.args.customerIds,
        })}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getConflicted,
    enabled:
      Boolean(options.getConflicted?.args.id) &&
      Boolean(options.getConflicted?.args.customerIds.length) &&
      Boolean(idCustomer),
  });

  const duplicateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/${variables.id}/duplicate`,
        { method: 'POST', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: (variables) =>
      variables.body.customers.map((item) =>
        PurchaseQueryKeys.quotePricingPolicies.base(item.id),
      ),
    retry: false,
    ...options.duplicate,
  });

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteServicePricingPolicies.get(
      options.get?.args.policyGroupId as string,
    ),
    queryFn: () =>
      options.get?.args
        ? callCoreApi<ISuccessApiResponse<IGetOneQuoteServicePricingPolicy>>(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/${options.get.args.policyGroupId}`,
          )
        : emptyQueryError(),
    ...options.get,
    enabled: Boolean(options.get?.args),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      callCoreApi<ISuccessApiResponse<ICreateQuoteServicePricingPolicyPayload>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/quote-service-pricing-policies/${id}`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.quoteServicePricingPolicies.base(),
      PurchaseQueryKeys.quoteDefaultServicePricingPolicies.base(),
      [
        ...(idCustomer
          ? PurchaseQueryKeys.quoteServicePricingPolicies.active(idCustomer)
          : []),
      ],
    ],
    retry: false,
    ...options.update,
  });

  return restrictApi(options, {
    create: createMutation,
    list: listQuery,
    remove: removeMutation,
    getConflicted: getConflictedQuery,
    duplicate: duplicateMutation,
    checkOverlap: checkOverlapMutation,
    paginationFetcher,
    get: getQuery,
    update: updateMutation,
    getActivePricingPolicies: getActivePricingPoliciesQuery,
  });
};
